import { useEffect, useState } from 'react';

import useFfmpeg, { ProgressInfo, VideoMetadata } from 'hooks/useFfmpeg';
import { createSeekPerformanceOptimizedVideoFromBlob } from 'utils/createVideoFromBlobs';

import { EditableVideo } from './types';
import { getThumbnails } from './utils';

export const usePreparedVideo = (
  sourceVideoBlob: Blob | null,
  onComplete?: (video: EditableVideo, metadata: VideoMetadata) => void,
  onProgress?: (progress: ProgressInfo | null) => void,
) => {
  const [preparedVideo, setPreparedVideo] = useState<EditableVideo | null>(
    null,
  );
  const [sourceMetadata, setSourceMetadata] = useState<VideoMetadata | null>(
    null,
  );
  const { loadFfmpeg, createProgressTracker, getVideoMetadata } = useFfmpeg();

  // Load the source video into ffmpeg for faster trimming and turn it into webm
  useEffect(() => {
    const prepareVideo = async () => {
      if (!sourceVideoBlob) return;

      const ffmpeg = await loadFfmpeg();

      const preMetadata = await getVideoMetadata(
        sourceVideoBlob,
        (progress) => {
          // Scale progress from 0-33% for the metadata gathering phase
          const scaledProgress = {
            ...progress,
            percentage: progress.percentage * 0.33,
          };
          onProgress?.({
            ...scaledProgress,
            message: 'Analyzing video details…',
          });
        },
      );

      // Show progress for starting video preparation
      onProgress?.({
        percentage: 34,
        time: '00:00:00',
        message: 'Optimizing video for trimming…',
      });

      const outputFilename = `prepared_video.mp4`;

      const video = await createSeekPerformanceOptimizedVideoFromBlob(
        sourceVideoBlob,
        outputFilename,
        ffmpeg,
        (progress) => {
          // Scale progress from 34-100% for the video conversion phase
          const scaledProgress = {
            ...progress,
            percentage: 34 + progress.percentage * 0.66,
          };
          onProgress?.({
            ...scaledProgress,
            message: 'Optimizing video for trimming…',
          });
        },
        createProgressTracker,
        preMetadata,
      );

      // Keep progress at 100% while getting final metadata
      onProgress?.({
        percentage: 100,
        time: '00:00:00',
        message: 'Finishing touches…',
      });

      const metadata = await getVideoMetadata(video.blob);

      // Set the video with metadata
      const videoWithMetadata: EditableVideo = {
        ...video,
        metadata,
      };

      setPreparedVideo(videoWithMetadata);
      setSourceMetadata(preMetadata);

      onComplete?.(videoWithMetadata, preMetadata);

      // Only clear progress after everything is complete
      onProgress?.(null);
    };
    if (!preparedVideo) {
      prepareVideo();
    }
  }, [sourceVideoBlob]);

  return { preparedVideo, sourceMetadata };
};

export const useThumbnails = (inputVideo: EditableVideo | null) => {
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const { loadFfmpeg } = useFfmpeg();
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);

  useEffect(() => {
    const generateThumbnails = async () => {
      if (!inputVideo) return;
      if (isGeneratingThumbnails) return;

      setIsGeneratingThumbnails(true);
      try {
        const thumbnails = await getThumbnails({
          inputVideo,
          loadFfmpeg,
        });
        setThumbnails(thumbnails);
      } finally {
        setIsGeneratingThumbnails(false);
      }
    };
    generateThumbnails();
  }, [loadFfmpeg, inputVideo]);

  return thumbnails;
};

export const useVideoCache = () => {
  const [videoCache, setVideoCache] = useState<Map<string, EditableVideo>>(
    new Map(),
  );

  const addToCache = (key: string, video: EditableVideo) => {
    setVideoCache((prev) => {
      prev.set(key, video);
      return prev;
    });
  };

  const getFromCache = (key: string): EditableVideo | undefined => {
    return videoCache.get(key);
  };

  return { addToCache, getFromCache, videoCache };
};
